/**
 * <XtsStaticMover> — non-interactive ghost mover at a fixed
 * `partPositionMm` along a chain.
 *
 * Used by `<XtsStopPositionMovers>` to visualise station stop targets
 * without contaminating the runtime model: these ghosts never appear
 * in `MoverConfig[]`, never participate in collision detection, never
 * fire selection / hover events, and never read from the
 * `MoverPositionStore`. They're pure geometry pinned at a static pose.
 *
 * Materials are cloned on mount so the per-instance `opacity` /
 * `emissive` mutation doesn't bleed into other movers sharing the same
 * GLB cache entry. Disposed + restored on unmount.
 */

import React, { Suspense, useEffect } from 'react';
import { Color, Mesh } from 'three';
import type { Material, MeshStandardMaterial } from 'three';
import type { GLTF } from 'three-stdlib';
import type { BuiltChain } from '../../geometry/ChainBuilder.js';
import { moverWorldAt } from '../../geometry/ChainBuilder.js';
import { useGltfClone } from './useGltfClone.js';
import { useMoverSidecar } from './useMoverSidecar.js';
import {
  DEFAULT_ORIGIN_CORRECTION,
  type OriginCorrection,
} from '../../assets/SidecarLoader.js';
import type { MoverType3D } from '../../types.js';

const DEG2RAD = Math.PI / 180;

interface XtsStaticMoverProps {
  chain: BuiltChain;
  partPositionMm: number;
  glbUrl: string;
  moverType?: MoverType3D;
  assetsBaseUrl?: string;
  /** When set, overrides the fetched sidecar's correction. */
  originCorrection?: OriginCorrection;
  /** Material opacity applied to the cloned GLB. Default 0.45. */
  opacity?: number;
  /**
   * Emissive tint hex (e.g. `'#38a355'`). When set, every standard
   * material in the GLB picks up the colour as a soft glow at intensity
   * 0.35 — useful to colour-code ghosts by their station.
   */
  tintColor?: string;
  /** Fallback wireframe size when the GLB hasn't loaded. */
  pathLengthMm?: number;
  heightMm?: number;
}

const XtsStaticMoverContent: React.FC<XtsStaticMoverProps> = ({
  chain,
  partPositionMm,
  glbUrl,
  moverType = 'AT9014_0055',
  assetsBaseUrl = '/models',
  originCorrection,
  opacity = 0.45,
  tintColor,
  pathLengthMm = 70,
  heightMm = 125,
}) => {
  const sidecar = useMoverSidecar(moverType, assetsBaseUrl);
  const correction =
    originCorrection ?? sidecar.correction ?? DEFAULT_ORIGIN_CORRECTION;
  const gltf = useGltfClone(glbUrl ?? '');

  // Clone-on-write: mutate copies of every material so the ghost
  // opacity / tint never leaks into normal mover instances rendered
  // from the same cache entry.
  useEffect(() => {
    if (!gltf) return;
    type Swap = {
      mesh: Mesh;
      original: Material | Material[];
      cloned: Material | Material[];
    };
    const swaps: Swap[] = [];
    const tint = tintColor ? new Color(tintColor) : null;
    const cloneOne = (m: Material): Material => {
      const c = m.clone();
      c.transparent = opacity < 1;
      c.opacity = opacity;
      c.depthWrite = opacity >= 0.99;
      if (tint && 'emissive' in c) {
        const std = c as MeshStandardMaterial;
        std.emissive.copy(tint);
        std.emissiveIntensity = 0.35;
      }
      return c;
    };
    gltf.scene.traverse((obj) => {
      const mesh = obj as Mesh;
      if (!mesh.isMesh || !mesh.material) return;
      const next: Material | Material[] = Array.isArray(mesh.material)
        ? mesh.material.map(cloneOne)
        : cloneOne(mesh.material);
      swaps.push({ mesh, original: mesh.material, cloned: next });
      mesh.material = next;
    });
    return () => {
      for (const { mesh, original, cloned } of swaps) {
        mesh.material = original;
        if (Array.isArray(cloned)) cloned.forEach((m) => m.dispose());
        else cloned.dispose();
      }
    };
  }, [gltf, opacity, tintColor]);

  const pose = moverWorldAt(chain, partPositionMm);
  if (!pose) return null;

  return (
    <group
      position={pose.positionMm}
      rotation={[0, 0, (pose.yawDeg * Math.PI) / 180]}
      // Block raycasts so ghosts can't be selected / hovered. Raycast
      // override is the cleanest opt-out — pointer-events on the GLB
      // children would still bubble up.
      raycast={() => null}
    >
      {!glbUrl || !gltf ? (
        <mesh position={[0, 0, 50]}>
          <boxGeometry args={[pathLengthMm, 50, heightMm]} />
          <meshBasicMaterial
            color="#3D88E0"
            wireframe
            transparent
            opacity={opacity}
          />
        </mesh>
      ) : (
        <group
          position={correction.translateMm}
          rotation={[
            correction.rotationDegEuler[0] * DEG2RAD,
            correction.rotationDegEuler[1] * DEG2RAD,
            correction.rotationDegEuler[2] * DEG2RAD,
            'XYZ',
          ]}
        >
          <primitive object={(gltf as GLTF).scene} />
        </group>
      )}
    </group>
  );
};

export const XtsStaticMover: React.FC<XtsStaticMoverProps> = (props) => (
  <Suspense fallback={null}>
    <XtsStaticMoverContent {...props} />
  </Suspense>
);
